// src/views/Representatives/AddFunderOfficer.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  Badge
} from 'reactstrap';
import axios from '../../api/Axios';

const AddFunderOfficer = () => {
  const { funderId } = useParams();
  const navigate = useNavigate();
  
  const [funder, setFunder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    title: '',
    phone_number: '',
    email_address: '',
    funder_id: funderId
  });

  const [emailValidation, setEmailValidation] = useState({
    isValid: null,
    message: '',
    isDomainValid: null,
    domainMessage: ''
  });

  useEffect(() => {
    const fetchFunderDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get('accounts/funders/list/');
        const funderData = response.data.find(f => f.id === parseInt(funderId));
        
        if (!funderData) {
          setError('Funder not found');
          return;
        }
        
        setFunder(funderData);
        setFormData(prev => ({ ...prev, funder_id: funderData.id }));
      } catch (error) {
        console.error('Error fetching funder details:', error);
        setError('Failed to load funder details');
      } finally {
        setLoading(false);
      }
    };

    fetchFunderDetails();
  }, [funderId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation errors when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateEmailDomain = (email) => {
    if (!email || !funder?.domain) return { isValid: null, message: '' };
    
    const emailDomain = email.split('@')[1];
    if (!emailDomain) return { isValid: false, message: 'Invalid email format' };
    
    const isValid = emailDomain.toLowerCase() === funder.domain.toLowerCase();
    return {
      isValid,
      message: isValid 
        ? '' // Remove success message for domain validation too
        : `✗ Email must use funder domain: @${funder.domain}`
    };
  };

  const checkEmailExists = async (email) => {
    if (!email) return { exists: false };
    
    try {
      const response = await axios.get(`accounts/check-email/?email=${email}`);
      return response.data;
    } catch (error) {
      console.error('Error checking email:', error);
      return { exists: false };
    }
  };

  const handleEmailBlur = async (e) => {
    const email = e.target.value.trim();
    
    if (!email) {
      setEmailValidation({
        isValid: null,
        message: '',
        isDomainValid: null,
        domainMessage: ''
      });
      return;
    }

    try {
      // Validate domain first
      const domainValidation = validateEmailDomain(email);
      
      // Only check email existence if domain is valid
      let emailExists = { exists: false };
      if (domainValidation.isValid) {
        emailExists = await checkEmailExists(email);
      }
      
      setEmailValidation({
        isValid: domainValidation.isValid && !emailExists.exists,
        message: emailExists.exists ? '✗ This email is already registered' : '', // Remove success message
        isDomainValid: domainValidation.isValid,
        domainMessage: domainValidation.message
      });
    } catch (error) {
      console.error('Error during email validation:', error);
      // Set validation state to null on error to avoid false positives
      setEmailValidation({
        isValid: null,
        message: 'Unable to verify email availability',
        isDomainValid: validateEmailDomain(email).isValid,
        domainMessage: validateEmailDomain(email).message
      });
    }
  };

  const validateForm = async () => {
    const errors = {};

    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }

    if (!formData.phone_number.trim()) {
      errors.phone_number = 'Phone number is required';
    }

    if (!formData.email_address.trim()) {
      errors.email_address = 'Email address is required';
    } else {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email_address)) {
        errors.email_address = 'Please enter a valid email address';
      } else {
        // Check domain validation
        const domainValidation = validateEmailDomain(formData.email_address);
        if (!domainValidation.isValid) {
          errors.email_address = `Email must use the funder domain: @${funder?.domain}`;
        } else {
          // Check if email exists (real-time check during validation)
          const emailExists = await checkEmailExists(formData.email_address);
          if (emailExists.exists) {
            errors.email_address = 'This email is already registered';
          }
        }
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Perform async validation
    const errors = await validateForm();
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setSubmitLoading(true);
      setError('');
      
      const payload = {
        ...formData,
        funder_id: parseInt(funderId)
      };
      
      console.log('Sending funder officer data:', payload);
      
      await axios.post('accounts/create-funder-officer/', payload);
      
      setSuccess('Funder officer created successfully! An activation email has been sent.');
      
      // Navigate back to manage funders after 3 seconds
      setTimeout(() => {
        navigate('/general-dashboard/funders');
      }, 3000);
      
    } catch (error) {
      console.error('Error creating funder officer:', error);
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          const errorMessages = Object.entries(error.response.data)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('; ');
          setError(`Validation errors: ${errorMessages}`);
        } else {
          setError(error.response.data);
        }
      } else {
        setError('Failed to create funder officer. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
        <p className="mt-2">Loading funder details...</p>
      </div>
    );
  }

  if (!funder) {
    return (
      <div className="text-center p-4">
        <Alert color="danger">Funder not found</Alert>
        <Button color="secondary" onClick={() => navigate('/general-dashboard/funders')}>
          Back to Manage Funders
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">Add Funder Officer</h2>
          <p className="text-muted">Create a new officer for {funder.name}</p>
        </div>
        <Button 
          color="secondary" 
          onClick={() => navigate('/general-dashboard/funders')}
          className="d-flex align-items-center"
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Manage Funders
        </Button>
      </div>

      {error && (
        <Alert color="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" className="mb-3">
          {success}
        </Alert>
      )}

      <Card>
        <CardBody>
          <CardTitle tag="h5" className="d-flex align-items-center">
            <i className="fas fa-user-tie me-2 text-primary"></i>
            Funder Officer Details
            <Badge color="info" className="ms-3">
              {funder.name} (@{funder.domain})
            </Badge>
          </CardTitle>

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="first_name">
                    First Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="first_name"
                    id="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.first_name}
                    placeholder="Enter first name"
                  />
                  {validationErrors.first_name && (
                    <div className="invalid-feedback">{validationErrors.first_name}</div>
                  )}
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label for="last_name">
                    Last Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="last_name"
                    id="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.last_name}
                    placeholder="Enter last name"
                  />
                  {validationErrors.last_name && (
                    <div className="invalid-feedback">{validationErrors.last_name}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="title">Title</Label>
                  <Input
                    type="text"
                    name="title"
                    id="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Finance Officer, Administrator"
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label for="phone_number">
                    Phone Number <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="tel"
                    name="phone_number"
                    id="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    invalid={!!validationErrors.phone_number}
                    placeholder="Enter phone number"
                  />
                  {validationErrors.phone_number && (
                    <div className="invalid-feedback">{validationErrors.phone_number}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="email_address">
                    Email Address <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="email"
                    name="email_address"
                    id="email_address"
                    value={formData.email_address}
                    onChange={handleInputChange}
                    onBlur={handleEmailBlur}
                    invalid={!!validationErrors.email_address}
                    placeholder={`Enter email address (must use @${funder.domain})`}
                  />
                  {validationErrors.email_address && (
                    <div className="invalid-feedback">{validationErrors.email_address}</div>
                  )}
                  
                  {/* Email validation feedback */}
                  {emailValidation.message && (
                    <div className={`mt-1 small ${emailValidation.isValid ? 'text-success' : 'text-danger'}`}>
                      {emailValidation.message}
                    </div>
                  )}
                  
                  {/* Domain validation feedback */}
                  {emailValidation.domainMessage && (
                    <div className={`mt-1 small ${emailValidation.isDomainValid ? 'text-success' : 'text-warning'}`}>
                      {emailValidation.domainMessage}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button 
                type="button" 
                color="secondary" 
                onClick={() => navigate('/general-dashboard/funders')}
                disabled={submitLoading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                color="primary" 
                disabled={submitLoading || emailValidation.isValid === false || emailValidation.isDomainValid === false}
                className="d-flex align-items-center"
              >
                {submitLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus me-2"></i>
                    Create Officer
                  </>
                )}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default AddFunderOfficer;
